import React, { useState } from 'react';
import { Alert, Badge, Row } from 'react-bootstrap';
import { invoiceAmountFormatter, niceDateFormatter } from '../../lib/helper/formatting';
import { buildLedgerRows, emptyLedgerState, reconcileLedger } from './ledger';
import { buildDuplicateVisibility } from './duplicates';

/**
 * The invoice's transaction ledger (MED-967/969).
 *
 * During phase 1 this sits ALONGSIDE Payment Activity rather than replacing it. The ledger is
 * written in parallel with the balance and read by nothing, so showing both is what lets the
 * people who know the invoice check that the two agree - a check the scheduled reconciliation
 * command cannot make, because it does not know what the invoice ought to say.
 *
 * MED-975 makes this the primary view and relabels Payment Activity as pre-ledger history, once
 * the balance is derived from these rows instead of written next to them.
 */
const LedgerTab = ({ invoice }) => {
  // MED-1023. Component state, so it resets with the page - opening any invoice starts with
  // duplicates hidden. `revealedIds` holds the originals whose marker has been clicked, which is
  // the only way to reveal anything: reveal is per group, so there is no global "show all".
  const [revealedIds, setRevealedIds] = useState([]);

  const toggleGroup = (id) => setRevealedIds((ids) => (ids.includes(id) ? ids.filter((existing) => existing !== id) : [...ids, id]));

  // Built from every transaction, then filtered - never the other way round. buildLedgerRows
  // walks the whole ledger to carry the running balance, giving a non-counting row the previous
  // balance; filter its input and the balance column silently changes.
  const allRows = buildLedgerRows(invoice?.invoiceTransactions);
  const view = buildDuplicateVisibility(allRows, { revealedOriginalIds: revealedIds });
  const reconciliation = reconcileLedger(invoice?.invoiceTransactions, invoice?.outstandingBalance);

  if (allRows.length === 0) {
    // Reached on most invoices - see emptyLedgerState. An empty ledger is the right answer for a
    // settled invoice and a fault for one that is still owed money, so it must not read the same
    // way in both cases.
    const empty = emptyLedgerState(invoice);

    return empty.isFault ? (
      <Alert variant={'danger'}>
        <i className={'fas fa-exclamation-triangle mr-2'} />
        {empty.message}
      </Alert>
    ) : (
      <p className={'text-muted'}>
        <i className={'fas fa-info-circle mr-2'} />
        {empty.message}
      </p>
    );
  }

  return (
    <div className={'px-3 py-2 mb-3'}>
      <Row className={'d-flex flex-grow-1'}>
        <table className={'table-sm flex-grow-1'}>
          <thead>
            <tr>
              <th>Date</th>
              <th>Type</th>
              <th>Detail</th>
              <th className={'text-right'}>Amount</th>
              <th className={'text-right'}>Balance</th>
              <th>Recorded by</th>
            </tr>
          </thead>
          <tbody>
            {view.rows.map((row) => (
              <tr key={row.id} className={row.badge ? 'text-muted' : undefined}>
                <td>{niceDateFormatter(row.transactionDate)}</td>
                <td>
                  {row.typeLabel}
                  {row.badge ? (
                    <Badge variant={'secondary'} className={'ml-2'} title={row.badgeTitle}>
                      {row.badge}
                    </Badge>
                  ) : null}
                  {/* MED-1023. With the duplicate rows gone this marker is the only thing on the
                      screen saying a re-report exists, so it is load-bearing rather than
                      decorative. It is also the only control that can bring the rows back - there
                      is no global "show all" - which is why duplicates.js refuses to hide a row
                      that has no marker to return it. */}
                  {row.duplicateCount > 0 ? (
                    <Badge
                      variant={'secondary'}
                      className={'ml-2 pointer'}
                      title={`This transaction was re-reported ${row.duplicateCount === 1 ? 'once' : `${row.duplicateCount} times`}. Click to ${row.duplicatesRevealed ? 'hide' : 'show'} the re-reports; they do not affect the balance.`}
                      onClick={() => toggleGroup(row.id)}>
                      {row.duplicateMarker}
                    </Badge>
                  ) : null}
                </td>
                <td className={'px-4'}>
                  {row.method?.name ?? ''}
                  {row.payee && row.payee !== 'MEDIACCOUNTS' ? (
                    /* Collected by somebody other than us - real money to them, none to us. */
                    <span className={'ml-2 text-muted'}>(to {row.payee.toLowerCase()})</span>
                  ) : null}
                </td>
                <td className={'text-right'}>{invoiceAmountFormatter(row.amount, '£')}</td>
                {/* A row that does not count leaves the balance where it was, so a duplicate is
                    visibly present and visibly inert. */}
                <td className={'text-right'}>{invoiceAmountFormatter(row.runningBalance, '£')}</td>
                <td>{row.recordedBy}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Row>

      {/* The reason this tab is worth having during phase 1. */}
      <Row className={'d-flex flex-grow-1 mt-3'}>
        {reconciliation.agrees ? (
          <span className={'text-success'}>
            <i className={'fas fa-check-circle mr-2'} />
            Ledger total {invoiceAmountFormatter(reconciliation.ledgerTotal, '£')} agrees with the outstanding balance.
          </span>
        ) : reconciliation.overpayment ? (
          <span className={'text-warning'}>
            <i className={'fas fa-exclamation-circle mr-2'} />
            Overpaid by {invoiceAmountFormatter(Math.abs(reconciliation.ledgerTotal), '£')}. The outstanding balance cannot go below zero, so it shows nothing
            owed. This is a refund to arrange, not a fault.
          </span>
        ) : (
          <span className={'text-danger'}>
            <i className={'fas fa-exclamation-triangle mr-2'} />
            Ledger total {invoiceAmountFormatter(reconciliation.ledgerTotal, '£')} does not match the outstanding balance of{' '}
            {invoiceAmountFormatter(reconciliation.storedBalance, '£')} (out by {invoiceAmountFormatter(Math.abs(reconciliation.difference), '£')}). The
            outstanding balance is still the figure being chased, so nothing is wrong with this invoice - but please report it.
          </span>
        )}
      </Row>
    </div>
  );
};

export default LedgerTab;
